/**
 * Privacy Policy Page
 * Noodle Studio
 */

import React from 'react';
import { BetaTag, BrandMark } from '../components/BrandMark';
import { ArrowLeft, ArrowRight, Shield, Database, Eye, Clock, UserCheck, Lock, Globe, Cookie, Mail } from 'lucide-react';
import { Link } from 'react-router-dom';
import { usePageMeta, PAGE_META } from '../hooks/usePageMeta';

interface PrivacyPageProps {
  onBack: () => void;
  onTerms: () => void;
  onAbout?: () => void;
}

export const PrivacyPage: React.FC<PrivacyPageProps> = ({ onBack, onTerms, onAbout }) => {
  usePageMeta(PAGE_META.privacy);
  const lastUpdated = 'February 4, 2026';

  return (
    <div className="fixed inset-0 z-[200] bg-cream overflow-auto">
      {/* The bar every page has: the way back, and whose page this is. The page's name heads the content. */}
      <header className="ns-pagebar">
        <div className="w-full max-w-4xl mx-auto flex items-center justify-between gap-3">
          <button onClick={onBack} className="ns-btn ns-btn-ghost ns-btn-sm shrink-0">
            <ArrowLeft size={14} aria-hidden="true" />
            Back
          </button>
          <Link to="/" className="flex items-center gap-2.5 rounded-full shrink-0" aria-label="Noodle Studio home">
            <BrandMark size={32} />
            <span className="text-[1.0625rem] font-bold tracking-[-0.02em] text-ink">Noodle Studio</span>
            <BetaTag />
          </Link>
        </div>
      </header>

      {/* Main Content */}
      <main className="px-4 sm:px-6 py-8">
        <div className="max-w-4xl mx-auto">
          {/* Hero Section */}
          <div className="ns-card p-6 sm:p-8 mb-8">
            <div className="flex items-center gap-3 mb-4">
              <span className="ns-mark ns-mark-lavender">
                <Shield aria-hidden="true" />
              </span>
              <div>
                <h1 className="ns-t-page">Privacy Policy</h1>
                <p className="text-ink-soft text-sm">Last updated: {lastUpdated}</p>
              </div>
            </div>
            <p className="text-ink-soft">
              Noodle Studio ("we", "us", or "our") is committed to protecting your privacy. 
              This Privacy Policy explains how we collect, use, disclose, and safeguard your information 
              when you use Noodle Studio.
            </p>
          </div>

          {/* Sections */}
          <div className="space-y-6">
            {/* Section 1: Information We Collect */}
            <section className="ns-card">
              <div className="flex items-center gap-3 mb-4">
                <Database className="w-5 h-5 shrink-0 text-lavender-deep" aria-hidden="true" />
                <h2 className="text-lg font-semibold text-ink">1. Information We Collect</h2>
              </div>
              <div className="text-ink-soft space-y-4">
                <div>
                  <h3 className="font-medium text-ink mb-2">Account Information</h3>
                  <ul className="list-disc list-inside space-y-1 ml-4">
                    <li>Name and email address</li>
                    <li>Authentication credentials (encrypted)</li>
                    <li>Profile information you provide</li>
                    <li>Payment information (processed by third-party providers)</li>
                  </ul>
                </div>
                <div>
                  <h3 className="font-medium text-ink mb-2">Usage Data</h3>
                  <ul className="list-disc list-inside space-y-1 ml-4">
                    <li>Features you use and actions you take</li>
                    <li>Content you create, upload, or generate</li>
                    <li>Workspace and project metadata</li>
                    <li>AI generation prompts and parameters</li>
                  </ul>
                </div>
                <div>
                  <h3 className="font-medium text-ink mb-2">Device & Technical Data</h3>
                  <ul className="list-disc list-inside space-y-1 ml-4">
                    <li>IP address and approximate location</li>
                    <li>Browser type and version</li>
                    <li>Device type and operating system</li>
                    <li>Referral URLs and page views</li>
                  </ul>
                </div>
                <div>
                  <h3 className="font-medium text-ink mb-2">Third-Party Data</h3>
                  <ul className="list-disc list-inside space-y-1 ml-4">
                    <li>Social media profile data (if you connect accounts)</li>
                    <li>OAuth authentication data from Google, Twitter, etc.</li>
                  </ul>
                </div>
              </div>
            </section>

            {/* Section 2: How We Use Your Information */}
            <section className="ns-card">
              <div className="flex items-center gap-3 mb-4">
                <Eye className="w-5 h-5 shrink-0 text-lavender-deep" aria-hidden="true" />
                <h2 className="text-lg font-semibold text-ink">2. How We Use Your Information</h2>
              </div>
              <div className="text-ink-soft space-y-3">
                <p>We use the information we collect to:</p>
                <ul className="list-disc list-inside space-y-1 ml-4">
                  <li><strong className="text-ink">Provide the Service:</strong> Create and manage your account, process AI generations, store your projects</li>
                  <li><strong className="text-ink">Improve the Service:</strong> Analyze usage patterns, fix bugs, develop new features</li>
                  <li><strong className="text-ink">Communicate:</strong> Send service updates, security alerts, and promotional messages (with consent)</li>
                  <li><strong className="text-ink">Security:</strong> Detect and prevent fraud, abuse, and unauthorized access</li>
                  <li><strong className="text-ink">Legal Compliance:</strong> Comply with legal obligations and respond to legal requests</li>
                  <li><strong className="text-ink">Billing:</strong> Process payments and manage subscriptions</li>
                </ul>
              </div>
            </section>

            {/* Section 3: Data Sharing */}
            <section className="ns-card">
              <div className="flex items-center gap-3 mb-4">
                <Globe className="w-5 h-5 shrink-0 text-lavender-deep" aria-hidden="true" />
                <h2 className="text-lg font-semibold text-ink">3. Data Sharing & Disclosure</h2>
              </div>
              <div className="text-ink-soft space-y-3">
                <p>We may share your information with:</p>
                <ul className="list-disc list-inside space-y-2 ml-4">
                  <li>
                    <strong className="text-ink">AI Service Providers:</strong> OpenAI, Google, and other AI providers to process your generation requests. 
                    These providers have their own privacy policies.
                  </li>
                  <li>
                    <strong className="text-ink">Cloud Infrastructure:</strong> Firebase and other cloud providers for data storage and processing.
                  </li>
                  <li>
                    <strong className="text-ink">Payment Processors:</strong> Stripe, Solana payment providers to process transactions.
                  </li>
                  <li>
                    <strong className="text-ink">Social Platforms:</strong> Twitter/X and other platforms when you choose to share content.
                  </li>
                  <li>
                    <strong className="text-ink">Legal Requirements:</strong> When required by law, subpoena, or to protect our rights and safety.
                  </li>
                  <li>
                    <strong className="text-ink">Business Transfers:</strong> In connection with mergers, acquisitions, or sale of assets.
                  </li>
                </ul>
                <p className="ns-well mt-4 p-4">
                  <strong className="text-ink">We do NOT:</strong> Sell your personal information to third parties for advertising purposes.
                </p>
              </div>
            </section>

            {/* Section 4: Data Retention */}
            <section className="ns-card">
              <div className="flex items-center gap-3 mb-4">
                <Clock className="w-5 h-5 shrink-0 text-lavender-deep" aria-hidden="true" />
                <h2 className="text-lg font-semibold text-ink">4. Data Retention</h2>
              </div>
              <div className="text-ink-soft space-y-3">
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="border-b border-cream-300">
                        <th className="text-left py-2 pr-4 text-ink">Data Type</th>
                        <th className="text-left py-2 text-ink">Retention Period</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-cream-300">
                      <tr>
                        <td className="py-2 pr-4">Account data (active accounts)</td>
                        <td className="py-2">Duration of account + 30 days</td>
                      </tr>
                      <tr>
                        <td className="py-2 pr-4">Project/workspace content</td>
                        <td className="py-2">Duration of account + 30 days</td>
                      </tr>
                      <tr>
                        <td className="py-2 pr-4">Deleted account data</td>
                        <td className="py-2">30 days (then permanently deleted)</td>
                      </tr>
                      <tr>
                        <td className="py-2 pr-4">Usage logs</td>
                        <td className="py-2">90 days</td>
                      </tr>
                      <tr>
                        <td className="py-2 pr-4">Security logs</td>
                        <td className="py-2">1 year</td>
                      </tr>
                      <tr>
                        <td className="py-2 pr-4">Payment records</td>
                        <td className="py-2">7 years (legal requirement)</td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              </div>
            </section>

            {/* Section 5: Your Rights */}
            <section className="ns-card">
              <div className="flex items-center gap-3 mb-4">
                <UserCheck className="w-5 h-5 shrink-0 text-lavender-deep" aria-hidden="true" />
                <h2 className="text-lg font-semibold text-ink">5. Your Rights</h2>
              </div>
              <div className="text-ink-soft space-y-3">
                <p>Depending on your location, you may have the following rights:</p>
                <ul className="list-disc list-inside space-y-2 ml-4">
                  <li><strong className="text-ink">Access:</strong> Request a copy of your personal data</li>
                  <li><strong className="text-ink">Correction:</strong> Request correction of inaccurate data</li>
                  <li><strong className="text-ink">Deletion:</strong> Request deletion of your data ("right to be forgotten")</li>
                  <li><strong className="text-ink">Portability:</strong> Receive your data in a machine-readable format</li>
                  <li><strong className="text-ink">Objection:</strong> Object to certain processing activities</li>
                  <li><strong className="text-ink">Restriction:</strong> Request limitation of processing</li>
                  <li><strong className="text-ink">Withdraw Consent:</strong> Withdraw previously given consent</li>
                </ul>
                <p className="mt-4">
                  To exercise these rights, write to us on the <Link to="/bugs" className="ns-link">Known issues</Link> page, signed in, and say it is a privacy request. We will respond within 30 days.
                </p>
              </div>
            </section>

            {/* Section 6: Security */}
            <section className="ns-card">
              <div className="flex items-center gap-3 mb-4">
                <Lock className="w-5 h-5 shrink-0 text-lavender-deep" aria-hidden="true" />
                <h2 className="text-lg font-semibold text-ink">6. Security Measures</h2>
              </div>
              <div className="text-ink-soft space-y-3">
                <p>We implement industry-standard security measures:</p>
                <ul className="list-disc list-inside space-y-1 ml-4">
                  <li><strong className="text-ink">Encryption:</strong> TLS 1.3 for data in transit, AES-256 for data at rest</li>
                  <li><strong className="text-ink">Authentication:</strong> Secure password hashing, OAuth 2.0, optional 2FA</li>
                  <li><strong className="text-ink">Access Control:</strong> Role-based access, principle of least privilege</li>
                  <li><strong className="text-ink">Infrastructure:</strong> SOC 2 compliant cloud providers (Firebase)</li>
                  <li><strong className="text-ink">Monitoring:</strong> Automated security monitoring and anomaly detection</li>
                  <li><strong className="text-ink">API Keys:</strong> User API keys are encrypted and stored securely server-side</li>
                </ul>
                <p data-tone="warn" className="ns-note block mt-4 p-4 rounded-lg text-base">
                  <strong>Important:</strong> While we implement robust security measures, no system is 100% secure. 
                  Please use strong passwords and protect your account credentials.
                </p>
              </div>
            </section>

            {/* Section 7: Cookies */}
            <section className="ns-card">
              <div className="flex items-center gap-3 mb-4">
                <Cookie className="w-5 h-5 shrink-0 text-lavender-deep" aria-hidden="true" />
                <h2 className="text-lg font-semibold text-ink">7. Cookies & Tracking</h2>
              </div>
              <div className="text-ink-soft space-y-3">
                <p>We use cookies and similar technologies for:</p>
                <ul className="list-disc list-inside space-y-2 ml-4">
                  <li>
                    <strong className="text-ink">Essential Cookies:</strong> Required for authentication, security, and core functionality. Cannot be disabled.
                  </li>
                  <li>
                    <strong className="text-ink">Functional Cookies:</strong> Remember your preferences and settings.
                  </li>
                  <li>
                    <strong className="text-ink">Analytics Cookies:</strong> Help us understand how you use the Service (via Firebase Analytics).
                  </li>
                </ul>
                <p className="mt-4">
                  You can manage cookie preferences in your browser settings. Note that disabling essential cookies 
                  may prevent the Service from functioning properly.
                </p>
              </div>
            </section>

            {/* Section 8: Children */}
            <section className="ns-card">
              <div className="flex items-center gap-3 mb-4">
                <Shield className="w-5 h-5 shrink-0 text-lavender-deep" aria-hidden="true" />
                <h2 className="text-lg font-semibold text-ink">8. Children's Privacy</h2>
              </div>
              <div className="text-ink-soft space-y-3">
                <p>
                  Our Service is not intended for children under 13 years of age. We do not knowingly collect 
                  personal information from children under 13. If you are a parent or guardian and believe 
                  your child has provided us with personal information, please contact us immediately.
                </p>
                <p>
                  Users between 13-18 years of age must have parental or guardian consent to use the Service.
                </p>
              </div>
            </section>

            {/* Section 9: International Transfers */}
            <section className="ns-card">
              <div className="flex items-center gap-3 mb-4">
                <Globe className="w-5 h-5 shrink-0 text-lavender-deep" aria-hidden="true" />
                <h2 className="text-lg font-semibold text-ink">9. International Data Transfers</h2>
              </div>
              <div className="text-ink-soft space-y-3">
                <p>
                  Your data may be processed in countries outside your residence, including the United States. 
                  We ensure appropriate safeguards are in place:
                </p>
                <ul className="list-disc list-inside space-y-1 ml-4">
                  <li>Standard Contractual Clauses (SCCs) for EU data transfers</li>
                  <li>Privacy Shield-compliant service providers where applicable</li>
                  <li>Data processing agreements with all third-party providers</li>
                </ul>
              </div>
            </section>

            {/* Section 10: Changes */}
            <section className="ns-card">
              <div className="flex items-center gap-3 mb-4">
                <Clock className="w-5 h-5 shrink-0 text-lavender-deep" aria-hidden="true" />
                <h2 className="text-lg font-semibold text-ink">10. Changes to This Policy</h2>
              </div>
              <div className="text-ink-soft space-y-3">
                <p>
                  We may update this Privacy Policy from time to time. We will notify you of significant 
                  changes by:
                </p>
                <ul className="list-disc list-inside space-y-1 ml-4">
                  <li>Posting the new Privacy Policy on this page</li>
                  <li>Updating the "Last updated" date</li>
                  <li>Sending an email notification for material changes</li>
                  <li>Displaying an in-app notification</li>
                </ul>
              </div>
            </section>

            {/* Section 11: Contact */}
            <section className="ns-card">
              <div className="flex items-center gap-3 mb-4">
                <Mail className="w-5 h-5 shrink-0 text-lavender-deep" aria-hidden="true" />
                <h2 className="text-lg font-semibold text-ink">11. Contact Us</h2>
              </div>
              <div className="text-ink-soft space-y-3">
                <p>For privacy-related inquiries:</p>
                <div className="ns-well p-4 space-y-2">
                  <p><strong className="text-ink">Noodle Studio</strong></p>
                  <p>Write to us on the <Link to="/bugs" className="ns-link">Known issues</Link> page, signed in, and say it is a privacy request. Reports are read by the people who run Noodle Studio and are not published until reviewed; a privacy request is answered there and never published.</p>
                </div>
                <p className="mt-4">
                  For EU residents, you also have the right to lodge a complaint with your local data protection authority.
                </p>
              </div>
            </section>
          </div>

          {/* Related Links */}
          <div className="ns-card mt-8">
            <h2 className="text-lg font-semibold text-ink mb-4">Related documents</h2>
            <div className="flex flex-wrap gap-3">
              <Link to="/terms" className="ns-btn ns-btn-soft">
                Terms of Service
                <ArrowRight size={16} aria-hidden="true" />
              </Link>
            </div>
          </div>
        </div>
      </main>

      {/* Footer */}
      <footer className="py-3 px-4 sm:px-6 border-t border-cream-300 bg-cream/70 backdrop-blur-sm">
        <div className="max-w-4xl mx-auto flex items-center justify-between gap-3">
          <p className="text-ink-soft text-xs font-medium">© 2026 Noodle Studio</p>
          <nav aria-label="Footer" className="flex items-center gap-1">
            <Link to="/about" className="ns-btn ns-btn-quiet ns-btn-xs">About</Link>
            <Link to="/terms" className="ns-btn ns-btn-quiet ns-btn-xs">Terms</Link>
          </nav>
        </div>
      </footer>
    </div>
  );
};
